/**
 * Base Analyzer Interface
 * 
 * This defines the contract that all device-specific analyzers must implement.
 */

import { AnalysisContext, DeviceInfo } from '../types';

/**
 * Interface for device analyzers
 */
export interface DeviceAnalyzer {
  /**
   * Checks if this analyzer can handle the given analysis context
   * @param context The analysis context
   * @returns True if this analyzer can handle the context, false otherwise
   */
  canAnalyze(context: AnalysisContext): boolean;
  
  /**
   * Analyzes the context to extract device information
   * @param context The analysis context
   * @returns Device information extracted from the context
   */
  analyze(context: AnalysisContext): Partial<DeviceInfo>;
  
  /**
   * Gets the priority of this analyzer (higher numbers = higher priority)
   * Used to determine which analyzer to use when multiple can handle the same context
   */
  getPriority(): number;
}

/**
 * Abstract base class for device analyzers
 */
export abstract class BaseDeviceAnalyzer implements DeviceAnalyzer {
  /**
   * Default priority for analyzers
   */
  protected priority = 0;
  
  /**
   * Gets the priority of this analyzer
   */
  getPriority(): number {
    return this.priority;
  }
  
  /**
   * Abstract method to check if this analyzer can handle the given context
   */
  abstract canAnalyze(context: AnalysisContext): boolean;
  
  /**
   * Abstract method to analyze the context
   */
  abstract analyze(context: AnalysisContext): Partial<DeviceInfo>;
}
